/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import moment from 'moment-timezone';

import { action } from '@ember/object';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { DynamicDatePrefix } from 'volta/models/constants/date-filters';
import { formatISODate, LOCAL_DATE_FORMAT } from "volta/utils/date-utils";
import type { TDate } from 'volta/models/types/calendar';

interface FiltersDateFilterComponentArgs {
}

export interface FiltersDateFilterSignature
  extends FilterComponentSignature<FiltersDateFilterComponentArgs> {
  Element: HTMLElement;
}

export default class FiltersDateFilterComponent extends Component<FiltersDateFilterSignature> {
  @tracked left?: string = this.values?.[0];
  @tracked right?: string = this.values?.[1];
  @tracked dynamicValue?: string;

  placeholder = moment.localeData().longDateFormat(LOCAL_DATE_FORMAT);

  get values() {
    return this.args.values ?? [];
  }

  get leftValue() {
    return this.dynamicValue ?? this.left ?? this.value;
  }

  get value() {
    return this.values?.length ? this.values[0] : undefined;
  }

  get rightValue() {
    return this.right ?? (this.values?.length === 2 ? this.values[1] : undefined);
  }

  get dateFilter() {
    return {
      startDate: isPresent(this.left) ? moment.utc(this.left).toDate() : undefined,
      endDate: isPresent(this.right) ? moment.utc(this.right).toDate() : undefined
    };
  }

  @action
  valuesDidChange({ startDate, endDate }: { startDate?: Date; endDate?: Date }) {
    const values: FiltersDateFilterSignature['Args']['values'] = [];
    if (startDate) {
      values.push(this.formatValue(startDate)!);
    }
    if (endDate) {
      values.push(this.formatValue(endDate)!);
    }

    if (values.length === 2) {
      this.args.onChange?.(values);
    }
  }

  @action
  valueDidChange(isFromCalendar: boolean, newValue?: Date | string) {
    const values: FiltersDateFilterSignature['Args']['values'] = isPresent(newValue) ? (newValue instanceof Date ? [this.formatValue(newValue) as string] : [newValue as string]) : [];
    this.args.onChange?.(values);

    if (isFromCalendar) {
      this.dynamicValue = undefined;
    }
  }

  @action
  handleUpdateValues(_el: HTMLElement, [[left, right] = []]: [string[] | undefined]) {
    if (this.left !== left) {
      this.left = left;
      if (this.left?.startsWith(DynamicDatePrefix) || this.left === undefined) {
        this.dynamicValue = this.left;
      }
    }
    if (this.right !== right) {
      this.right = right;
    }
  }

  private formatValue(value?: TDate) {
    return isPresent(value) ? formatISODate(value) : undefined;
  }

  @action
  handleDynamicValue(value: string) {
    this.dynamicValue = value;
    this.valueDidChange(false, value);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Filters::DateFilter': typeof FiltersDateFilterComponent;
    'filters/date-filter': typeof FiltersDateFilterComponent;
  }
}
